import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import cartService from '../services/cartService';
import guestCartService from '../services/guestCartService';
import { searchFoods } from '../services/foodService';
import MenuItemDetail from './MenuItemDetail';
import { trackInstacartCheckoutClick } from '../utils/analytics';
import { toFraction } from '../utils/quantityFormat';
import { formatConvertedAmount } from '../utils/weightUnits';
import { accumulateIngredient, finalizeAggregatedItem } from '../utils/weightUnits';
import { useWeightUnit } from '../hooks/useWeightUnit';
import './ShoppingCart.css';

const ShoppingCart = () => {
  const weightUnit = useWeightUnit();
  const [cartEntries, setCartEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedRecipes, setCollapsedRecipes] = useState(new Set());
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());
  const [removedIngredients, setRemovedIngredients] = useState(new Set());
  const [customItems, setCustomItems] = useState([]);
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [instacartLoading, setInstacartLoading] = useState(false);
  const [savedLists, setSavedLists] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [saveListName, setSaveListName] = useState('');
  const [foodSearchOpen, setFoodSearchOpen] = useState(false);
  const [foodQuery, setFoodQuery] = useState('');
  const [foodResults, setFoodResults] = useState([]);
  const [foodLoading, setFoodLoading] = useState(false);
  const [foodTypeFilter, setFoodTypeFilter] = useState('all');
  const foodSearchTimer = useRef(null);

  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const isGuest = !user;
  const svc = isGuest ? guestCartService : cartService;

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCart = async () => {
    try {
      setLoading(true);
      const data = await svc.getCart();
      setCartEntries(data.menuEntries || []);
      setCustomItems(data.customItems || []);
    } catch (error) {
      console.error('Failed to load cart:', error);
      toast.error('Failed to load shopping list');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEntry = async (menuItemId) => {
    try {
      const data = await svc.removeFromCart(menuItemId);
      setCartEntries(data.menuEntries || []);
      setCollapsedRecipes(prev => {
        const next = new Set(prev);
        next.delete(menuItemId);
        return next;
      });
      toast.success('Removed from list');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleUpdateCount = async (menuItemId, newCount) => {
    if (newCount < 1) {
      handleRemoveEntry(menuItemId);
      return;
    }
    try {
      const data = await svc.updateCount(menuItemId, newCount);
      setCartEntries(data.menuEntries || []);
    } catch (error) {
      toast.error('Failed to update count');
    }
  };

  const toggleCollapse = (menuItemId) => {
    setCollapsedRecipes(prev => {
      const next = new Set(prev);
      if (next.has(menuItemId)) {
        next.delete(menuItemId);
      } else {
        next.add(menuItemId);
      }
      return next;
    });
  };

  const toggleIngredientCheck = (key) => {
    setCheckedIngredients(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Aggregate ingredients from non-collapsed cart entries
  const aggregatedIngredients = useMemo(() => {
    const ingredientMap = new Map();

    for (const entry of cartEntries) {
      const menuItem = entry.menuItem;
      if (!menuItem || !menuItem.ingredients) continue;
      const itemId = entry.menuItemId || menuItem._id;
      if (collapsedRecipes.has(itemId)) continue;

      const indices = entry.selectedIngredients?.length
        ? entry.selectedIngredients
        : menuItem.ingredients.map((_, i) => i);
      const originalServings = Math.max(1, menuItem.servings || 4);
      const preferredServings = entry.preferredServings || originalServings;
      const scaleFactor = preferredServings / originalServings;
      const count = entry.count || 1;

      for (const index of indices) {
        if (index < 0 || index >= menuItem.ingredients.length) continue;
        const ing = menuItem.ingredients[index];
        if (ing.isSpice && !entry.includeSpices) continue;
        if (ing.isOil && !entry.includeOils) continue;

        const qty = (ing.quantity || 0) * scaleFactor * count;
        accumulateIngredient(ingredientMap, ing.name, qty, ing.unit, {
          sources: new Set([menuItem.title]),
          source: menuItem.title
        });
      }
    }

    return Array.from(ingredientMap.values())
      .map(item => finalizeAggregatedItem(item, weightUnit))
      .filter(item => !removedIngredients.has(item.key))
      .map(item => ({ ...item, sources: Array.from(item.sources) }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [cartEntries, collapsedRecipes, removedIngredients, weightUnit]);

  const totalItems = aggregatedIngredients.length + customItems.length;
  const checkedCount = checkedIngredients.size;
  const progress = totalItems > 0 ? checkedCount / totalItems : 0;

  const handleAddCustomItem = async () => {
    if (!customName.trim()) return;
    try {
      const data = await svc.addCustomItem(
        customName.trim(),
        customQty.trim() || '1',
        customUnit.trim()
      );
      setCustomItems(data.customItems || []);
      setCustomName('');
      setCustomQty('');
      setCustomUnit('');
    } catch (error) {
      console.error('Failed to add custom item:', error);
      toast.error('Failed to add item');
    }
  };

  // Debounced food search
  useEffect(() => {
    if (!foodQuery || foodQuery.trim().length < 2) {
      setFoodResults([]);
      return;
    }
    if (foodSearchTimer.current) clearTimeout(foodSearchTimer.current);
    foodSearchTimer.current = setTimeout(async () => {
      try {
        setFoodLoading(true);
        const data = await searchFoods(foodQuery.trim(), {
          limit: 20,
          dataType: foodTypeFilter !== 'all' ? foodTypeFilter : undefined
        });
        setFoodResults(data.foods || []);
      } catch (err) {
        console.error('Food search error:', err);
      } finally {
        setFoodLoading(false);
      }
    }, 300);
    return () => { if (foodSearchTimer.current) clearTimeout(foodSearchTimer.current); };
  }, [foodQuery, foodTypeFilter]);

  const handleAddFoodItem = async (food) => {
    try {
      const unit = food.servingSize?.unit ? `${food.servingSize.amount || 100}${food.servingSize.unit}` : 'serving';
      const data = await svc.addCustomItem(food.description, '1', unit);
      setCustomItems(data.customItems || []);
      toast.success(`Added "${food.description}"`);
    } catch (error) {
      console.error('Failed to add food item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleRemoveCustomItem = async (id) => {
    try {
      const data = await svc.removeCustomItem(id);
      setCustomItems(data.customItems || []);
      setCheckedIngredients(prev => {
        const next = new Set(prev);
        next.delete(`custom_${id}`);
        return next;
      });
    } catch (error) {
      console.error('Failed to remove custom item:', error);
      toast.error('Failed to remove item');
    }
  };

  const handleRemoveIngredient = (ingredientKey) => {
    setRemovedIngredients(prev => {
      const next = new Set(prev);
      next.add(ingredientKey);
      return next;
    });
    // Also remove from checked set
    setCheckedIngredients(prev => {
      const next = new Set(prev);
      next.delete(ingredientKey);
      return next;
    });
  };

  const customUpdateTimers = useRef({});

  const handleUpdateCustomItem = (id, field, value) => {
    // Update local state immediately for responsive UI
    setCustomItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    // Debounce the server update
    if (customUpdateTimers.current[id]) clearTimeout(customUpdateTimers.current[id]);
    customUpdateTimers.current[id] = setTimeout(async () => {
      try {
        await svc.updateCustomItem(id, { [field]: value });
      } catch (error) {
        console.error('Failed to update custom item:', error);
      }
    }, 500);
  };

  const handleInstacart = async () => {
    try {
      setInstacartLoading(true);
      trackInstacartCheckoutClick(
        cartEntries.length + customItems.length,
        isGuest ? 'guest_cart' : 'cart'
      );

      if (isGuest) {
        // Guest mode: build ingredients list client-side, use public endpoint
        const ingredients = [
          ...aggregatedIngredients.map(ing => ({
            name: ing.name,
            quantity: ing.quantity || 1,
            unit: ing.unit || 'each'
          })),
          ...customItems.map(item => ({
            name: item.name,
            quantity: parseFloat(item.quantity) || 1,
            unit: item.unit || 'each'
          }))
        ];
        if (ingredients.length === 0) {
          toast.error('No ingredients to shop for');
          return;
        }
        const menuCount = cartEntries.length;
        const customCount = customItems.length;
        let recipeTitle;
        if (menuCount === 1 && customCount === 0) {
          recipeTitle = cartEntries[0].menuItem?.title || 'Shopping list';
        } else if (menuCount > 0) {
          recipeTitle = `FreshFitLab – ${menuCount} recipe${menuCount > 1 ? 's' : ''}${customCount > 0 ? ` + ${customCount} item${customCount > 1 ? 's' : ''}` : ''}`;
        } else {
          recipeTitle = `FreshFitLab – ${customCount} item${customCount > 1 ? 's' : ''}`;
        }
        const data = await guestCartService.sendToInstacart(ingredients, recipeTitle);
        window.open(data.url, '_blank', 'noopener,noreferrer');
        toast.success('Instacart list created!');
      } else {
        // Authenticated mode: server aggregates ingredients from cart
        const itemsToSend = customItems.map(item => ({
          name: item.name,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit || 'each'
        }));
        const data = await cartService.sendToInstacart(itemsToSend);
        window.open(data.url, '_blank', 'noopener,noreferrer');
        toast.success('Instacart list created!');
      }
    } catch (error) {
      console.error('Instacart error:', error);
      toast.error('Failed to create Instacart list');
    } finally {
      setInstacartLoading(false);
    }
  };

  const buildSavePayload = () => {
    const items = customItems.map(i => ({
      name: i.name,
      quantity: parseFloat(i.quantity) || 1,
      unit: i.unit || 'each'
    }));
    const menuItemIds = cartEntries
      .map(entry => entry.menuItemId || entry.menuItem?._id)
      .filter(Boolean);
    return { items, menuItemIds };
  };

  const handleSaveListNew = async () => {
    if (!saveListName.trim()) return;
    try {
      const { items, menuItemIds } = buildSavePayload();
      await cartService.saveList(saveListName.trim(), items, menuItemIds);
      toast.success('List saved!');
      setShowSaveModal(false);
      setSaveListName('');
    } catch (error) {
      if (error.message?.includes('maximum')) {
        toast.error(error.message);
      } else {
        toast.error('Failed to save list');
      }
    }
  };

  const handleOverwriteList = async (listId, listName) => {
    try {
      const { items, menuItemIds } = buildSavePayload();
      await cartService.updateList(listId, listName, items, menuItemIds);
      toast.success(`Updated "${listName}"`);
      setShowSaveModal(false);
      setSaveListName('');
    } catch (error) {
      toast.error('Failed to update list');
    }
  };

  const handleOpenSaveModal = async () => {
    if (isGuest) {
      toast('Sign in to save your shopping lists', { icon: '🔒', duration: 3000 });
      return;
    }
    setSaveListName(`Shopping List - ${new Date().toLocaleDateString()}`);
    // Fetch existing lists so user can overwrite
    try {
      const data = await cartService.getSavedLists();
      setSavedLists(data.lists || []);
    } catch (e) {
      setSavedLists([]);
    }
    setShowSaveModal(true);
  };

  const handleLoadSavedLists = async () => {
    if (isGuest) {
      toast('Sign in to load saved lists', { icon: '🔒', duration: 3000 });
      return;
    }
    try {
      const data = await cartService.getSavedLists();
      setSavedLists(data.lists || []);
      setShowLoadModal(true);
    } catch (error) {
      toast.error('Failed to load saved lists');
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.replace('gs://freshmenulab', 'https://storage.googleapis.com/freshmenulab');
  };

  if (loading) {
    return (
      <div className="shopping-cart-page">
        <div className="cart-loading">
          <div className="cart-spinner"></div>
          <p>Loading your shopping list...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-cart-page">
      {isGuest && (
        <div className="cart-preview-banner">
          <i className="fas fa-info-circle"></i>
          <span><Link to="/menu">Sign in</Link> to save your shopping lists across devices</span>
        </div>
      )}
      <div className="cart-container">
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-title">
            <i className="fas fa-shopping-cart"></i>
            <h1>Shopping List</h1>
          </div>
          <div className="cart-header-actions">
            <button className="cart-action-btn" onClick={handleOpenSaveModal} title="Save list">
              <i className="fas fa-save"></i>
              <span>Save</span>
            </button>
            <button className="cart-action-btn" onClick={handleLoadSavedLists} title="Load saved list">
              <i className="fas fa-folder-open"></i>
              <span>Load</span>
            </button>
          </div>
        </div>

        {cartEntries.length === 0 && customItems.length === 0 ? (
          <div className="cart-empty-state">
            <i className="fas fa-cart-plus cart-empty-icon"></i>
            <h2>Your shopping list is empty</h2>
            <p>Browse recipes and tap "Add to list" to get started</p>
            <Link to="/menu" className="cart-browse-btn">
              <i className="fas fa-utensils"></i> Browse Recipes
            </Link>
          </div>
        ) : (
          <>
            {/* Recipe Cards Section */}
            {cartEntries.length > 0 && (
              <section className="cart-recipes-section">
                <h2 className="cart-section-title">Recipes ({cartEntries.length})</h2>
                <div className="cart-recipes-list">
                  {cartEntries.map(entry => {
                    const menuItem = entry.menuItem;
                    const itemId = entry.menuItemId || menuItem?._id;
                    const isCollapsed = collapsedRecipes.has(itemId);
                    const ingredientCount = menuItem?.ingredients?.length || 0;
                    const imageUrl = getImageUrl(menuItem?.image);

                    return (
                      <div key={itemId} className={`cart-recipe-card ${isCollapsed ? 'collapsed' : ''}`}>
                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt={menuItem?.title}
                            className="cart-recipe-image cart-recipe-clickable"
                            onClick={() => menuItem && setSelectedMenuItem(menuItem)}
                          />
                        )}
                        {!imageUrl && (
                          <div
                            className="cart-recipe-image-placeholder cart-recipe-clickable"
                            onClick={() => menuItem && setSelectedMenuItem(menuItem)}
                          >
                            <i className="fas fa-utensils"></i>
                          </div>
                        )}
                        <div className="cart-recipe-info">
                          <h3
                            className="cart-recipe-title cart-recipe-clickable"
                            onClick={() => menuItem && setSelectedMenuItem(menuItem)}
                          >{menuItem?.title || 'Unknown recipe'}</h3>
                          <span className="cart-recipe-meta">
                            {entry.count}x servings &middot; {ingredientCount} ingredients
                            {isCollapsed && <span className="cart-hidden-label"> (hidden)</span>}
                          </span>
                        </div>
                        <div className="cart-recipe-controls">
                          <button
                            className={`cart-icon-btn ${isCollapsed ? 'muted' : ''}`}
                            onClick={() => toggleCollapse(itemId)}
                            title={isCollapsed ? 'Show ingredients' : 'Hide ingredients'}
                          >
                            <i className={`fas ${isCollapsed ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                          <button
                            className="cart-icon-btn danger"
                            onClick={() => entry.count <= 1 ? handleRemoveEntry(itemId) : handleUpdateCount(itemId, entry.count - 1)}
                            title={entry.count <= 1 ? 'Remove' : 'Decrease'}
                          >
                            <i className={`fas ${entry.count <= 1 ? 'fa-trash' : 'fa-minus'}`}></i>
                          </button>
                          <span className="cart-count-display">{entry.count}</span>
                          <button
                            className="cart-icon-btn"
                            onClick={() => handleUpdateCount(itemId, entry.count + 1)}
                            title="Increase"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Aggregated Ingredients Section */}
            <section className="cart-ingredients-section">
              <div className="cart-ingredients-header">
                <h2 className="cart-section-title">
                  Ingredients ({aggregatedIngredients.length})
                </h2>
                <div className="cart-ingredients-header-actions">
                  {collapsedRecipes.size > 0 && (
                    <span className="cart-hidden-count">
                      {collapsedRecipes.size} hidden
                    </span>
                  )}
                  {totalItems > 0 && (
                    <button
                      className="cart-bulk-check-btn"
                      onClick={() => {
                        if (checkedIngredients.size === totalItems) {
                          setCheckedIngredients(new Set());
                        } else {
                          const allKeys = new Set([
                            ...aggregatedIngredients.map(i => i.key),
                            ...customItems.map(i => `custom_${i.id}`)
                          ]);
                          setCheckedIngredients(allKeys);
                        }
                      }}
                    >
                      <i className={`fas ${checkedIngredients.size === totalItems ? 'fa-square' : 'fa-check-double'}`}></i>
                      {checkedIngredients.size === totalItems ? 'Uncheck All' : 'Check All'}
                    </button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {totalItems > 0 && (
                <div className="cart-progress">
                  <div className="cart-progress-bar">
                    <div className="cart-progress-fill" style={{ width: `${progress * 100}%` }}></div>
                  </div>
                  <span className="cart-progress-text">{checkedCount} of {totalItems} checked</span>
                </div>
              )}

              {/* Ingredient rows */}
              <div className="cart-ingredients-list">
                {aggregatedIngredients.map(ingredient => {
                  const isChecked = checkedIngredients.has(ingredient.key);
                  return (
                    <div
                      key={ingredient.key}
                      className={`cart-ingredient-row ${isChecked ? 'checked' : ''}`}
                      onClick={() => toggleIngredientCheck(ingredient.key)}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleIngredientCheck(ingredient.key)}
                        className="cart-ingredient-checkbox"
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="cart-ingredient-info">
                        <span className={`cart-ingredient-name ${isChecked ? 'strikethrough' : ''}`}>
                          {ingredient.name}
                        </span>
                        {ingredient.sources.length > 0 && (
                          <span className="cart-ingredient-sources">
                            {ingredient.sources.slice(0, 2).join(', ')}
                            {ingredient.sources.length > 2 && ` +${ingredient.sources.length - 2}`}
                          </span>
                        )}
                      </div>
                      <span className={`cart-ingredient-qty ${isChecked ? 'strikethrough' : ''}`}>
                        {ingredient.mass
                          ? `${formatConvertedAmount(ingredient.quantity, ingredient.unit)} ${ingredient.unit || ''}`
                          : `${toFraction(ingredient.quantity)} ${ingredient.unit || ''}`}
                      </span>
                      <button
                        className="cart-ingredient-remove"
                        onClick={e => { e.stopPropagation(); handleRemoveIngredient(ingredient.key); }}
                        title="Remove from list"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  );
                })}

                {/* Custom items */}
                {customItems.length > 0 && (
                  <div className="cart-custom-items-header">Custom Items</div>
                )}
                {customItems.map(item => {
                  const key = `custom_${item.id}`;
                  const isChecked = checkedIngredients.has(key);
                  return (
                    <div
                      key={item.id}
                      className={`cart-ingredient-row custom-editable ${isChecked ? 'checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleIngredientCheck(key)}
                        className="cart-ingredient-checkbox"
                      />
                      <div className="cart-ingredient-info">
                        <span className={`cart-ingredient-name ${isChecked ? 'strikethrough' : ''}`}>
                          {item.name}
                        </span>
                      </div>
                      <div className="cart-custom-edit-fields" onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          className="cart-custom-edit-qty"
                          value={item.quantity}
                          onChange={e => handleUpdateCustomItem(item.id, 'quantity', e.target.value)}
                          title="Quantity"
                        />
                        <input
                          type="text"
                          className="cart-custom-edit-unit"
                          value={item.unit}
                          onChange={e => handleUpdateCustomItem(item.id, 'unit', e.target.value)}
                          title="Unit"
                          placeholder="unit"
                        />
                      </div>
                      <button
                        className="cart-custom-remove"
                        onClick={e => { e.stopPropagation(); handleRemoveCustomItem(item.id); }}
                        title="Remove"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Add Items Section */}
            <section className="cart-add-items-section">
              {/* Custom Item Row */}
              <h3>Add Item</h3>
              <div className="cart-add-custom-row">
                <input
                  type="text"
                  placeholder="Item name"
                  value={customName}
                  onChange={e => setCustomName(e.target.value)}
                  className="cart-custom-input cart-custom-name"
                  onKeyDown={e => e.key === 'Enter' && handleAddCustomItem()}
                />
                <input
                  type="text"
                  placeholder="Qty"
                  value={customQty}
                  onChange={e => setCustomQty(e.target.value)}
                  className="cart-custom-input cart-custom-qty"
                  onKeyDown={e => e.key === 'Enter' && handleAddCustomItem()}
                />
                <input
                  type="text"
                  placeholder="Unit"
                  value={customUnit}
                  onChange={e => setCustomUnit(e.target.value)}
                  className="cart-custom-input cart-custom-unit"
                  onKeyDown={e => e.key === 'Enter' && handleAddCustomItem()}
                />
                <button
                  className="cart-add-custom-btn"
                  onClick={handleAddCustomItem}
                  disabled={!customName.trim()}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>

              {/* Collapsible Food Database Search */}
              <div className="cart-food-search-section">
                <button
                  className="cart-food-search-toggle"
                  onClick={() => setFoodSearchOpen(!foodSearchOpen)}
                >
                  <i className={`fas fa-chevron-${foodSearchOpen ? 'up' : 'down'}`}></i>
                  <i className="fas fa-database"></i>
                  Search Food Database
                </button>

                {foodSearchOpen && (
                  <div className="cart-food-search-panel">
                    <div className="cart-food-search-input-row">
                      <div className="cart-food-search-input-wrapper">
                        <i className="fas fa-search"></i>
                        <input
                          type="text"
                          placeholder="Search foods (e.g., chicken breast, granola...)"
                          value={foodQuery}
                          onChange={e => setFoodQuery(e.target.value)}
                          className="cart-food-search-input"
                          autoFocus
                        />
                        {foodQuery && (
                          <button className="cart-food-clear-btn" onClick={() => { setFoodQuery(''); setFoodResults([]); }}>
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="cart-food-type-filter">
                      <button
                        className={`cart-food-filter-btn ${foodTypeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setFoodTypeFilter('all')}
                      >All</button>
                      <button
                        className={`cart-food-filter-btn ${foodTypeFilter === 'unbranded' ? 'active' : ''}`}
                        onClick={() => setFoodTypeFilter('unbranded')}
                      ><i className="fas fa-apple-alt"></i> Generic</button>
                      <button
                        className={`cart-food-filter-btn ${foodTypeFilter === 'branded' ? 'active' : ''}`}
                        onClick={() => setFoodTypeFilter('branded')}
                      ><i className="fas fa-tag"></i> Branded</button>
                    </div>

                    <div className="cart-food-results">
                      {foodLoading ? (
                        <div className="cart-food-loading">
                          <i className="fas fa-spinner fa-spin"></i> Searching...
                        </div>
                      ) : foodResults.length > 0 ? (
                        foodResults.map(food => (
                          <div key={food.fdcId} className="cart-food-item">
                            <div className="cart-food-item-info">
                              <span className="cart-food-item-name">{food.description}</span>
                              <span className="cart-food-item-meta">
                                {food.category || ''}
                                {food.brandOwner && ` · ${food.brandOwner}`}
                              </span>
                            </div>
                            {food.nutrients && (
                              <div className="cart-food-item-macros">
                                <span>{Math.round(food.nutrients.protein || 0)}g P</span>
                                <span>{Math.round(food.nutrients.calories || 0)} cal</span>
                              </div>
                            )}
                            <button
                              className="cart-food-add-btn"
                              onClick={() => handleAddFoodItem(food)}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>
                        ))
                      ) : foodQuery.length >= 2 ? (
                        <div className="cart-food-empty">No foods found for "{foodQuery}"</div>
                      ) : (
                        <div className="cart-food-empty">Type at least 2 characters to search</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Actions */}
            <section className="cart-actions">
              <button
                className="cart-instacart-btn"
                onClick={handleInstacart}
                disabled={instacartLoading || (aggregatedIngredients.length === 0 && customItems.length === 0)}
              >
                {instacartLoading ? (
                  <>
                    <div className="cart-btn-spinner"></div>
                    Creating Instacart list...
                  </>
                ) : (
                  <>
                    <img src="/images/Instacart_Carrot.png" alt="Instacart" className="cart-instacart-logo" />
                    Shop with Instacart
                  </>
                )}
              </button>

              {checkedIngredients.size > 0 && (
                <button
                  className="cart-clear-checked-btn"
                  onClick={() => setCheckedIngredients(new Set())}
                >
                  Uncheck All Items
                </button>
              )}
              {removedIngredients.size > 0 && (
                <button
                  className="cart-restore-removed-btn"
                  onClick={() => setRemovedIngredients(new Set())}
                >
                  <i className="fas fa-undo"></i> Restore {removedIngredients.size} Removed Item{removedIngredients.size > 1 ? 's' : ''}
                </button>
              )}
              <button
                className="cart-clear-all-btn"
                onClick={async () => {
                  try {
                    if (isGuest) {
                      guestCartService.clearCart();
                    } else {
                      // Remove all menu entries
                      for (const entry of cartEntries) {
                        const id = entry.menuItemId || entry.menuItem?._id;
                        if (id) await cartService.removeFromCart(id);
                      }
                      // Remove all custom items
                      for (const item of customItems) {
                        await cartService.removeCustomItem(item.id);
                      }
                    }
                    setCartEntries([]);
                    setCustomItems([]);
                    setCheckedIngredients(new Set());
                    setRemovedIngredients(new Set());
                    setCollapsedRecipes(new Set());
                    toast.success('Shopping list cleared');
                  } catch (error) {
                    console.error('Failed to clear list:', error);
                    toast.error('Failed to clear list');
                    await loadCart();
                  }
                }}
              >
                <i className="fas fa-trash-alt"></i> Clear Entire List
              </button>
            </section>
          </>
        )}
      </div>

      {/* Save List Modal */}
      {showSaveModal && (
        <div className="cart-modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="cart-modal cart-save-modal" onClick={e => e.stopPropagation()}>
            <h3>Save Shopping List</h3>

            {/* Save as new */}
            <div className="cart-save-new-section">
              <label className="cart-save-label">Save as new list</label>
              <div className="cart-save-new-row">
                <input
                  type="text"
                  placeholder="List name"
                  value={saveListName}
                  onChange={e => setSaveListName(e.target.value)}
                  className="cart-modal-input cart-save-new-input"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleSaveListNew()}
                />
                <button
                  className="cart-modal-confirm"
                  onClick={handleSaveListNew}
                  disabled={!saveListName.trim()}
                >
                  Save New
                </button>
              </div>
            </div>

            {/* Overwrite existing */}
            {savedLists.length > 0 && (
              <div className="cart-save-existing-section">
                <label className="cart-save-label">Or overwrite an existing list</label>
                <div className="cart-saved-lists">
                  {savedLists.map(list => (
                    <div
                      key={list.id}
                      className="cart-saved-list-item cart-save-overwrite-item"
                      onClick={() => handleOverwriteList(list.id, list.name)}
                    >
                      <div className="cart-saved-list-info">
                        <span className="cart-saved-list-name">{list.name}</span>
                        <span className="cart-saved-list-meta">
                          {list.recipeCount > 0 && `${list.recipeCount} recipe${list.recipeCount > 1 ? 's' : ''}`}
                          {list.recipeCount > 0 && list.itemCount > 0 && ' · '}
                          {list.itemCount > 0 && `${list.itemCount} item${list.itemCount > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <span className="cart-saved-list-date">
                        {new Date(list.createdAt).toLocaleDateString()}
                      </span>
                      <span className="cart-overwrite-icon" title="Overwrite this list">
                        <i className="fas fa-save"></i>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cart-modal-actions">
              <button className="cart-modal-cancel" onClick={() => setShowSaveModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Load List Modal */}
      {showLoadModal && (
        <div className="cart-modal-overlay" onClick={() => setShowLoadModal(false)}>
          <div className="cart-modal" onClick={e => e.stopPropagation()}>
            <h3>Saved Lists</h3>
            {savedLists.length === 0 ? (
              <p className="cart-modal-empty">No saved lists yet</p>
            ) : (
              <div className="cart-saved-lists">
                {savedLists.map(list => (
                  <div
                    key={list.id}
                    className="cart-saved-list-item"
                    onClick={async () => {
                      try {
                        const data = await cartService.loadList(list.id);
                        let menusAdded = 0;
                        let itemsAdded = 0;

                        // Re-add menu items to the cart (recipe cards + their ingredients)
                        if (data.menuItemIds && data.menuItemIds.length > 0) {
                          for (const menuId of data.menuItemIds) {
                            try {
                              await cartService.addMenuToCart(menuId);
                              menusAdded++;
                            } catch (addErr) {
                              // May already be in cart or deleted — non-fatal
                              console.warn(`Could not re-add menu ${menuId}:`, addErr.message);
                            }
                          }
                        }

                        // Re-add custom items (these are only the manually added items, not recipe ingredients)
                        if (data.items && data.items.length > 0) {
                          for (const item of data.items) {
                            try {
                              await cartService.addCustomItem(
                                item.name,
                                item.quantity?.toString() || '1',
                                item.unit || ''
                              );
                              itemsAdded++;
                            } catch (addErr) {
                              console.warn('Could not re-add custom item:', addErr.message);
                            }
                          }
                        }

                        // Single reload to reflect everything
                        await loadCart();

                        const totalLoaded = menusAdded + itemsAdded;
                        toast.success(`Loaded "${list.name}" (${menusAdded} recipe${menusAdded !== 1 ? 's' : ''}${itemsAdded > 0 ? `, ${itemsAdded} item${itemsAdded !== 1 ? 's' : ''}` : ''})`);
                        setShowLoadModal(false);
                      } catch (err) {
                        toast.error('Failed to load list');
                      }
                    }}
                  >
                    <div className="cart-saved-list-info">
                      <span className="cart-saved-list-name">{list.name}</span>
                      <span className="cart-saved-list-meta">
                        {list.recipeCount > 0 && `${list.recipeCount} recipe${list.recipeCount > 1 ? 's' : ''}`}
                        {list.recipeCount > 0 && list.itemCount > 0 && ' · '}
                        {list.itemCount > 0 && `${list.itemCount} item${list.itemCount > 1 ? 's' : ''}`}
                      </span>
                    </div>
                    <span className="cart-saved-list-date">
                      {new Date(list.createdAt).toLocaleDateString()}
                    </span>
                    <button
                      className="cart-saved-list-delete"
                      title="Delete list"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await cartService.deleteList(list.id);
                          setSavedLists(prev => prev.filter(l => l.id !== list.id));
                          toast.success(`Deleted "${list.name}"`);
                        } catch (err) {
                          toast.error('Failed to delete list');
                        }
                      }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="cart-modal-actions">
              <button className="cart-modal-cancel" onClick={() => setShowLoadModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {selectedMenuItem && (
        <MenuItemDetail
          item={selectedMenuItem}
          onClose={() => setSelectedMenuItem(null)}
        />
      )}
    </div>
  );
};

export default ShoppingCart;
